import { loadRuntimeConfig } from "../config/runtimeConfig.js";
import { getSessionToken } from "../auth/session.js";

// Single fetch seam for the whole backoffice. Every call waits for runtime
// config first, so the API target always comes from /runtime-config.json
// (or whatever the reverse proxy/DNS points that path to)  -  never from a
// value baked into the JS bundle at build time.

const BASE_KIND_TO_CONFIG_FIELD = Object.freeze({
  api: "apiBaseUrl",
  auth: "authBaseUrl",
  trackingRead: "trackingReadBaseUrl",
});

export class ApiClientError extends Error {
  /**
   * `body` carries the parsed JSON error payload when the server sent one -
   * the backoffice controllers return `{ message: "..." }` in Thai for 400 and
   * 409 responses (a duplicate approval, a job that is not ready, a field over
   * its length limit), and without this the UI could only ever show a generic
   * "something went wrong" in place of the one sentence that tells an operator
   * what to do next. Null whenever the response had no parseable JSON body.
   *
   * Never populated for a network failure, and never carries request headers
   * or the request payload - the Authorization header must not reach an error
   * boundary or a log.
   */
  constructor(message, { status = null, requestId = null, cause = null, body = null } = {}) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.requestId = requestId;
    this.cause = cause;
    this.body = body;
  }
}

/** Joins a base URL and a path with exactly one slash between them. */
export function joinUrl(baseUrl, path) {
  const trimmedBase = String(baseUrl ?? "").replace(/\/+$/, "");
  const trimmedPath = String(path ?? "").replace(/^\/+/, "");
  if (!trimmedPath) {
    return trimmedBase || "/";
  }
  return `${trimmedBase}/${trimmedPath}`;
}

function generateRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `req_${crypto.randomUUID()}`;
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Performs a backend request against a runtime-config-resolved base URL.
 * Always awaits runtime config first  -  this is what lets an IP/server
 * migration take effect on next page refresh with no rebuild.
 *
 * @param {string} path - path appended to the resolved base URL.
 * @param {object} [options]
 * @param {"api"|"auth"|"trackingRead"} [options.base]
 * @param {typeof fetch} [options.fetchImpl] - injectable for tests.
 */
export async function apiRequest(path, options = {}) {
  const { base = "api", headers, fetchImpl, responseType, ...rest } = options;
  const configField = BASE_KIND_TO_CONFIG_FIELD[base];
  if (!configField) {
    throw new ApiClientError(`Unknown API base kind: ${base}`);
  }

  // Fails closed: if runtime config is missing/invalid, this rejects with
  // RuntimeConfigError before any network call is attempted. The same
  // fetchImpl override (used by tests to avoid a real network) applies to
  // both the config load and the backend call so they observe one mock.
  const config = await loadRuntimeConfig(fetchImpl ? { fetchImpl } : undefined);
  const baseUrl = config[configField];
  if (!baseUrl) {
    throw new ApiClientError(`Runtime config has no value for ${configField}`);
  }

  const url = joinUrl(baseUrl, path);
  const requestId = generateRequestId();
  const token = getSessionToken();

  // P146: a multipart upload must NOT carry a hand-written Content-Type -
  // only the browser can append the correct `boundary=` parameter, and
  // forcing application/json here made the body unparseable on the server.
  // Detected from the body itself rather than an opt-in flag so no caller can
  // forget it.
  const isFormDataBody = typeof FormData !== "undefined" && rest.body instanceof FormData;

  const requestHeaders = {
    ...(isFormDataBody ? {} : { "Content-Type": "application/json" }),
    "X-Request-Id": requestId,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  const doFetch = fetchImpl ?? (typeof fetch === "function" ? fetch.bind(globalThis) : undefined);
  if (typeof doFetch !== "function") {
    throw new ApiClientError("No fetch implementation available", { requestId });
  }

  let response;
  try {
    response = await doFetch(url, { ...rest, headers: requestHeaders });
  } catch (networkError) {
    // Never include headers/body in the thrown error  -  avoids leaking the
    // Authorization header or request payload into logs/error boundaries.
    throw new ApiClientError("Network request failed", {
      requestId,
      cause: networkError?.message,
    });
  }

  if (!response.ok) {
    // Best effort only: a body that is missing, empty, or not JSON leaves
    // `body` null rather than turning a 409 into an unhandled parse error.
    let errorBody = null;
    const errorContentType = response.headers?.get?.("content-type") ?? "";
    if (errorContentType.includes("application/json")) {
      try {
        errorBody = await response.json();
      } catch {
        errorBody = null;
      }
    }

    throw new ApiClientError(`Request failed with status ${response.status}`, {
      status: response.status,
      requestId,
      body: errorBody,
    });
  }

  if (responseType === "blob") {
    const contentType = response.headers?.get?.("content-type") ?? "application/octet-stream";
    return { blob: await response.blob(), contentType };
  }

  const contentType = response.headers?.get?.("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  return response.text();
}

export const apiClient = {
  get: (path, options) => apiRequest(path, { ...options, method: "GET" }),
  // P141R16: real binary reads (e.g. the passport document image) - never
  // routed through response.json()/response.text(), which would corrupt
  // binary bytes. Returns { blob, contentType } so a caller can build a
  // real object URL (URL.createObjectURL) for an <img>, never a data: URI
  // built by hand.
  getBlob: (path, options) => apiRequest(path, { ...options, method: "GET", responseType: "blob" }),
  post: (path, body, options) =>
    apiRequest(path, { ...options, method: "POST", body: JSON.stringify(body) }),
  // P146: real multipart/form-data upload (passport/identity document). The
  // FormData is passed through untouched so the browser sets the multipart
  // boundary itself - see the isFormDataBody note in apiRequest.
  postForm: (path, formData, options) =>
    apiRequest(path, { ...options, method: "POST", body: formData }),
  put: (path, body, options) =>
    apiRequest(path, { ...options, method: "PUT", body: JSON.stringify(body) }),
  patch: (path, body, options) =>
    apiRequest(path, { ...options, method: "PATCH", body: JSON.stringify(body) }),
  delete: (path, options) => apiRequest(path, { ...options, method: "DELETE" }),
};
